import { NavLink } from 'react-router-dom';
import { Space } from 'antd';
import { useContext, useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import { formatDistanceToNowStrict } from 'date-fns';
import { ACC_OWNER, TEAM_MANAGER } from 'constants/roles';
import { DEFAULT_IMG_USERS } from 'constants/urls';
import { CONTENT_TYPES } from 'constants/playlists';

import { SessionContext } from 'contexts/session';
import { UserInvites } from 'contexts/users/types';

import ButtonIcon from 'components/buttonIcon';
import Checkbox from 'components/checkbox';
import Switch from 'components/switch';
import Tooltip from 'components/tooltip';
import { Cell, EmptyCell } from 'components/Table';

import { randomColor } from 'utils/colors';
import {
  ActionButtonsContainer,
  ContainerDesktop,
  ContainerMobile,
  CustomRow,
  OwnerStarImg,
  PendingLetterAvatar,
  PendingUserImage,
  UserDataContainer,
} from './styles';
import {
  AccountCell,
  AccountStatusContainer,
  AccountStatusDot,
} from 'pages/Accounts/components/AccountsTable/styles';
import StaffUserSettingsDrawer from './StaffUserSettingsDrawer';
import { SwitchActions, SwitchLicenseActions } from './types';
import { UserTeamContainer } from 'pages/Users/components/InviteUsers/AdjustUsersSettings/styles';
import { UserTeamsPayload } from './types';
import OwnerStarSvg from 'assets/images/ownerStar.png';
import useSkillDiveToggleState from 'hooks/useSkillDiveToggleState';
import { SKILL_DIVE_STAFF_TOGGLE_TOOLTIPS } from 'constants/skill_dive';
import { WRAPPING_TOOLTIP_STYLE } from 'constants/components/tooltip';

export interface StaffUserRowProps extends UserInvites {
  onSwitchManager: (user_invite_id: string, teamsData: UserTeamsPayload[]) => void;
  onSwitchOwner?: ({ id, checked }: SwitchActions) => void;
  onSwitchLicense: ({ ids, checked }: SwitchLicenseActions) => void;
  onSelectRow: (id: string) => void;
  onRemoveUser: (ids: string[], name: string[]) => void;
  onCancelInvitation: (ids: string[], name: string[]) => void;
  onResendInvitation: (ids: string[]) => void;
  onSwitchAddon: (assigned_to?: string, addon?: string, license_addon_id?: string) => void;
  active?: boolean;
  role: string;
  row: UserInvites;
  rowSelected: boolean;
  tableGridConfig: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  uaa_data: any; // update with uaa data types,
  updated_at: string;
  accountPage?: boolean;
}

const StaffUserRow = ({
  onRemoveUser,
  onSelectRow,
  onSwitchLicense,
  onSwitchManager,
  onSwitchOwner,
  onCancelInvitation,
  onResendInvitation,
  onSwitchAddon,
  account,
  account_name,
  email,
  id,
  is_active,
  license,
  role,
  row,
  rowSelected,
  tableGridConfig,
  uaa_data,
  account_avaliable_licences,
  last_login,
  invited_at,
  accountPage,
  user,
  teams,
  account_available_addons,
  license_addons,
}: StaffUserRowProps): JSX.Element => {
  const { actions, isImpersonated } = useContext(SessionContext);
  const isRoleManager = role === TEAM_MANAGER;
  const isRoleAccOwner = role === ACC_OWNER;

  const loginOrInvitedDate = last_login || invited_at;

  const roleTooltipWarning = (
    <p className="text-center text-nowrap m-0">
      Users can only have one role assigned.
      <br />
      Changing a role will replace any existing roles.
    </p>
  );

  // Staff can always delete users
  const canRemoveUser = true;

  const isManagerSwitchDisabled = teams.length === 0;

  // Staff can always unassign a Skill Dive license, same as canRemoveUser above.
  const addonsSwitchStatus = useSkillDiveToggleState({
    account: { account_available_addons },
    user: { license, license_addons },
    can_unnasign_license: true,
    tooltips: SKILL_DIVE_STAFF_TOGGLE_TOOLTIPS,
  });

  const getLicenseSwitchStatus = () => {
    const switchStatus = {
      disabled: false,
      tooltip: !license ? 'Assign License' : 'Remove License',
    };
    if (!license) {
      if (
        account_avaliable_licences !== 'unlimited' &&
        parseInt(account_avaliable_licences) === 0
      ) {
        switchStatus.disabled = true;
        switchStatus.tooltip = 'There are no licenses available on this account';
      }
    } else if (addonsSwitchStatus.assignedId) {
      switchStatus.disabled = false;
      switchStatus.tooltip = `If the user's plan license is removed, the Skill Dive license will also be removed`;
    }
    return switchStatus;
  };

  const licenseSwitchStatus = getLicenseSwitchStatus();

  const licenseSwitchTooltipWarning = (
    <p className="text-center m-0">{licenseSwitchStatus.tooltip}</p>
  );

  const addonsSwitchTooltipWarning = (
    <p className="text-center m-0">{addonsSwitchStatus.tooltip}</p>
  );

  const statusTagStates = {
    past_due: { bgColor: '#F28815', label: 'Past Due' },
    expired: { bgColor: '#EE3B00', label: 'Expired' },
    active: null,
  };
  const statusTagState = statusTagStates[row.account_status];

  const handleSwitchAddon = () => {
    if (addonsSwitchStatus.assignedId) {
      onSwitchAddon(null, null, addonsSwitchStatus.assignedId);
    } else {
      const skillDiveAddon = account_available_addons?.find(
        (addon) => addon.name === CONTENT_TYPES.SKILL_DIVE
      );
      onSwitchAddon(user, skillDiveAddon?.id, null);
    }
  };

  useEffect(() => {
    getLicenseSwitchStatus();
  }, []);
  return (
    <>
      <CustomRow columns={tableGridConfig} collapsed={true}>
        {/* Checkbox */}
        <Cell style={{ justifyContent: 'flex-start', paddingLeft: 0 }}>
          <Checkbox checked={rowSelected} onChange={() => onSelectRow(id)} controlled />
        </Cell>

        {/* User personal info */}
        <Cell>
          <div className="d-flex align-items-center justiy-left position-relative">
            {isRoleAccOwner && (
              <Tooltip placement="right" title="Account owner">
                <OwnerStarImg src={OwnerStarSvg} />
              </Tooltip>
            )}
            {is_active &&
            uaa_data?.profile?.data?.avatar &&
            uaa_data?.profile?.data?.avatar !== DEFAULT_IMG_USERS ? (
              <PendingUserImage src={uaa_data?.profile?.data?.avatar} alt={email} />
            ) : (
              <PendingLetterAvatar color={randomColor(email)}>
                {email && email[0].toUpperCase()}
              </PendingLetterAvatar>
            )}
            <UserDataContainer>
              {is_active && (
                <>
                  <NavLink to={`/user/${user}`}>
                    <p>{`${uaa_data?.profile?.data.first_name} ${uaa_data?.profile?.data.last_name}`}</p>
                    <ContainerDesktop>
                      <p>{email}</p>
                    </ContainerDesktop>
                  </NavLink>
                </>
              )}
              {!is_active && <p>{email}</p>}
              <ContainerMobile>
                {Boolean(license) && <span className="badge badge-light">LICENSE</span>}
              </ContainerMobile>
            </UserDataContainer>
          </div>
        </Cell>
        <>
          {/* Account Info */}
          {!!account_name && !accountPage && (
            <ContainerDesktop>
              <Cell
                style={{
                  flexFlow: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <NavLink
                  to={`/account/${account}`}
                  style={{
                    color: '#7965C6',
                    fontWeight: '700',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '150px',
                  }}
                >
                  {account_name}
                </NavLink>
                <AccountCell
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'baseline',
                  }}
                >
                  {statusTagState && (
                    <AccountStatusContainer>
                      <AccountStatusDot bgColor={statusTagState.bgColor} />
                      <span>{statusTagState.label}</span>
                    </AccountStatusContainer>
                  )}
                </AccountCell>
              </Cell>
            </ContainerDesktop>
          )}

          {/* Owner switch */}
          <ContainerDesktop>
            <Cell style={{ minWidth: '60px' }} className="d-none d-md-table-cell bg-white">
              <Tooltip
                overlayInnerStyle={WRAPPING_TOOLTIP_STYLE}
                placement="bottomLeft"
                destroyTooltipOnHide
                title={roleTooltipWarning}
              >
                <span style={{ cursor: 'auto' }}>
                  <Switch
                    defaultChecked={isRoleAccOwner}
                    style={{ pointerEvents: 'auto' }}
                    onChange={(checked: boolean) => onSwitchOwner({ id: id, checked })}
                  />
                </span>
              </Tooltip>
            </Cell>
          </ContainerDesktop>

          {/* Team Info */}
          {!isMobile && (
            <Cell
              style={{
                fontWeight: '500',
                whiteSpace: 'initial',
              }}
            >
              <div style={{ width: '100%' }}>
                {teams.map((team, index) => (
                  <UserTeamContainer
                    key={team.team_id}
                    borderBottom={index < teams.length - 1}
                    width="85"
                  >
                    <div>{team.name}</div>
                    <Tooltip
                      overlayInnerStyle={WRAPPING_TOOLTIP_STYLE}
                      placement="bottomLeft"
                      destroyTooltipOnHide
                      title={
                        isManagerSwitchDisabled ? 'User must belong to a team.' : roleTooltipWarning
                      }
                    >
                      <span style={{ cursor: isManagerSwitchDisabled ? 'not-allowed' : 'auto' }}>
                        <Switch
                          checked={team.is_manager}
                          disabled={isManagerSwitchDisabled}
                          onChange={(checked) =>
                            onSwitchManager(id, [{ team_id: team.team_id, is_manager: checked }])
                          }
                        />
                      </span>
                    </Tooltip>
                  </UserTeamContainer>
                ))}
                {teams.length === 0 && <span className="text-truncate">Unassigned teams</span>}
              </div>
            </Cell>
          )}

          {/* License switch  */}
          <ContainerDesktop>
            <Cell
              style={{
                minWidth: '60px',
                gap: is_active && '10px',
                padding: is_active && '0px',
                justifyContent: 'space-between',
              }}
            >
              <Tooltip
                overlayInnerStyle={WRAPPING_TOOLTIP_STYLE}
                placement="bottom"
                destroyTooltipOnHide
                title={licenseSwitchTooltipWarning}
              >
                <span style={{ cursor: licenseSwitchStatus.disabled ? 'not-allowed' : 'auto' }}>
                  <Switch
                    checked={Boolean(license)}
                    style={{ pointerEvents: licenseSwitchStatus.disabled ? 'none' : 'auto' }}
                    disabled={licenseSwitchStatus.disabled}
                    onChange={(checked: boolean) => onSwitchLicense({ ids: [id], checked })}
                  />
                </span>
              </Tooltip>
              {is_active && (
                <Tooltip
                  overlayInnerStyle={WRAPPING_TOOLTIP_STYLE}
                  placement="bottom"
                  destroyTooltipOnHide
                  title={addonsSwitchTooltipWarning}
                >
                  <span style={{ cursor: addonsSwitchStatus.disabled ? 'not-allowed' : 'auto' }}>
                    <Switch
                      defaultChecked={Boolean(addonsSwitchStatus.assignedId)}
                      checked={Boolean(addonsSwitchStatus.assignedId)}
                      style={{ pointerEvents: addonsSwitchStatus.disabled ? 'none' : 'auto' }}
                      disabled={addonsSwitchStatus.disabled}
                      onChange={handleSwitchAddon}
                    />
                  </span>
                </Tooltip>
              )}
            </Cell>
          </ContainerDesktop>
        </>
        {/*  Last active */}
        {accountPage && (
          <ContainerDesktop>
            <Cell>
              <p
                className="mb-0 text-left"
                style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
              >
                {loginOrInvitedDate ? (
                  formatDistanceToNowStrict(new Date(loginOrInvitedDate), { addSuffix: true })
                ) : (
                  <EmptyCell />
                )}
              </p>
            </Cell>
          </ContainerDesktop>
        )}

        {/* Actions on users */}
        <Cell style={{ justifyContent: 'flex-end' }}>
          <ActionButtonsContainer>
            <Space>
              {is_active ? (
                <ContainerDesktop>
                  <Tooltip placement="bottomRight" title={'Impersonate as this user'}>
                    <div
                      style={{
                        cursor: isImpersonated ? 'not-allowed' : 'pointer',
                        marginRight: '15px',
                      }}
                    >
                      <ButtonIcon
                        style={{ pointerEvents: isImpersonated ? 'none' : 'all' }}
                        disabled={isImpersonated}
                        $secondary
                        size={'small'}
                        icon={'Key'}
                        onClick={() => {
                          actions.impersonate(email);
                        }}
                      />
                    </div>
                  </Tooltip>
                  <Tooltip placement="bottomRight" title={'Remove user'} destroyTooltipOnHide>
                    <div style={{ cursor: canRemoveUser ? 'pointer' : 'not-allowed' }}>
                      <ButtonIcon
                        $secondary
                        size="small"
                        icon="Trash"
                        style={{ pointerEvents: canRemoveUser ? 'all' : 'none' }}
                        disabled={!canRemoveUser}
                        onClick={() =>
                          onRemoveUser([id], [uaa_data?.profile?.data.first_name || email])
                        }
                      />
                    </div>
                  </Tooltip>
                </ContainerDesktop>
              ) : (
                <>
                  <ContainerDesktop>
                    <Tooltip placement="bottomRight" title="Resend invitation">
                      <ButtonIcon
                        $secondary
                        size="small"
                        icon="Resend"
                        onClick={() => onResendInvitation([id])}
                      />
                    </Tooltip>
                  </ContainerDesktop>
                  <ContainerDesktop>
                    <Tooltip placement="bottomRight" title="Cancel invitation">
                      <ButtonIcon
                        $secondary
                        size="small"
                        icon="Cancel"
                        onClick={() => onCancelInvitation([id], [email])}
                      />
                    </Tooltip>
                  </ContainerDesktop>
                </>
              )}
              <ContainerMobile>
                <StaffUserSettingsDrawer
                  data={row}
                  disableManager={isManagerSwitchDisabled}
                  isAccOwner={isRoleAccOwner}
                  licenseSwitchStatus={licenseSwitchStatus}
                  isManager={isRoleManager}
                  onRemoveUser={onRemoveUser}
                  onSwitchLicense={onSwitchLicense}
                  onSwitchManager={onSwitchManager}
                  onSwitchOwner={onSwitchOwner}
                  roleTooltipText={roleTooltipWarning}
                  statusTagState={statusTagState}
                  onCancelInvite={() => onCancelInvitation([id], [email])}
                  onResendInvite={() => onResendInvitation([id])}
                />
              </ContainerMobile>
            </Space>
          </ActionButtonsContainer>
        </Cell>
      </CustomRow>
    </>
  );
};

export default StaffUserRow;
